/**
 * Offline tag verification - lokalna sesja weryfikacji kolczyków.
 * Sesja żyje w IndexedDB, sync po powrocie sieci.
 */

import { openDb } from "./db";
import type { ActiveTagItem, ActiveNotFoundItem, ProgramStatus } from "../../types";

const DB_NAME = "HerdManagementDB";
const STORE_NAME = "tag_verification_session";

export type OfflineTagVerificationSession = {
  id: "active"; // zawsze tylko jedna aktywna sesja
  program_statuses: ProgramStatus[];
  tags: ActiveTagItem[];
  not_found: ActiveNotFoundItem[];
  created_at: number;
  updated_at: number;
  synced: boolean; // czy sesja została zsynchronizowana z serwerem
  server_session_id?: number; // ID sesji na serwerze (jeśli już POST start)
};

export async function ensureTagVerificationStore(): Promise<void> {
  const currentConn = await openDb();
  if (!currentConn) return;

  const currentVersion = currentConn.db.version;
  currentConn.close();

  if (currentVersion >= 3) {
    // Store already exists
    return;
  }

  // Need to upgrade to version 3
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 3);

    request.onerror = () => {
      console.error("[offlineTagVerification] Failed to upgrade DB:", request.error);
      reject(request.error);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
        console.log("[offlineTagVerification] Created tag_verification_session store");
      }
    };

    request.onsuccess = () => {
      request.result.close();
      resolve();
    };

    request.onblocked = () => {
      console.warn("[offlineTagVerification] Upgrade blocked");
      reject(new Error("DB upgrade blocked"));
    };
  });
}

export async function saveOfflineSession(
  session: Omit<OfflineTagVerificationSession, "id">
): Promise<boolean> {
  await ensureTagVerificationStore();
  
  const conn = await openDb();
  if (!conn) return false;

  try {
    const tx = conn.db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);

    const entry: OfflineTagVerificationSession = {
      id: "active",
      ...session,
    };

    store.put(entry);

    return new Promise((resolve) => {
      tx.oncomplete = () => {
        console.log("[offlineTagVerification] Saved session");
        resolve(true);
      };
      tx.onerror = () => {
        console.error("[offlineTagVerification] Failed to save session:", tx.error);
        resolve(false);
      };
    });
  } finally {
    conn.close();
  }
}

export async function loadOfflineSession(): Promise<OfflineTagVerificationSession | null> {
  await ensureTagVerificationStore();
  
  const conn = await openDb();
  if (!conn) return null;

  try {
    return new Promise((resolve) => {
      const tx = conn.db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const request = store.get("active");

      request.onsuccess = () => {
        resolve(request.result || null);
      };

      request.onerror = () => {
        console.error("[offlineTagVerification] Failed to load session:", request.error);
        resolve(null);
      };
    });
  } finally {
    conn.close();
  }
}

export async function clearOfflineSession(): Promise<void> {
  await ensureTagVerificationStore();
  
  const conn = await openDb();
  if (!conn) return;

  try {
    const tx = conn.db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    store.delete("active");

    await new Promise<void>((resolve) => {
      tx.oncomplete = () => {
        console.log("[offlineTagVerification] Cleared session");
        resolve();
      };
      tx.onerror = () => {
        console.error("[offlineTagVerification] Failed to clear session:", tx.error);
        resolve();
      };
    });
  } finally {
    conn.close();
  }
}

export async function markSessionSynced(serverSessionId?: number): Promise<void> {
  const session = await loadOfflineSession();
  if (!session) return;

  session.synced = true;
  session.updated_at = Date.now();
  if (serverSessionId !== undefined) {
    session.server_session_id = serverSessionId;
  }

  await saveOfflineSession(session);
}

/**
 * Synchronizacja sesji z serwerem po powrocie sieci.
 * 1. Jeśli nie ma sesji na serwerze → POST start
 * 2. PUT aktualnego stanu
 */
export async function syncTagVerificationSession(
  apiPost: (path: string, options: unknown) => Promise<{ data?: unknown; error?: unknown }>,
  apiPut: (path: string, options: unknown) => Promise<{ data?: unknown; error?: unknown }>,
  apiGet: (path: string) => Promise<{ data?: unknown; error?: unknown }>,
): Promise<{ success: boolean; error?: string }> {
  const session = await loadOfflineSession();
  if (!session) {
    return { success: true }; // Brak sesji offline
  }

  if (session.synced) {
    return { success: true }; // Już zsynchronizowana
  }

  try {
    // Sprawdź czy jest sesja na serwerze
    const serverCheck = await apiGet("/api/tag-verifications/active");

    let serverSessionExists = false;
    if (!serverCheck.error && serverCheck.data) {
      const data = serverCheck.data as { session?: unknown };
      serverSessionExists = Boolean(data.session);
    }

    // Jeśli nie ma sesji na serwerze, utwórz ją
    if (!serverSessionExists) {
      const startResult = await apiPost("/api/tag-verifications/active", {
        body: { program_statuses: session.program_statuses },
      });

      if (startResult.error) {
        return { success: false, error: "Failed to start server session" };
      }
    }

    // PUT aktualnego stanu
    const putResult = await apiPut("/api/tag-verifications/active", {
      body: {
        tags: session.tags,
        not_found: session.not_found,
      },
    });

    if (putResult.error) {
      return { success: false, error: "Failed to sync session state" };
    }

    // Oznacz jako zsynchronizowaną
    await markSessionSynced();

    console.log("[offlineTagVerification] Session synced successfully");
    return { success: true };
  } catch (error) {
    return { success: false, error: String(error) };
  }
}
